/**
 * Error handling utilities
 * Provides consistent error messages throughout the application
 */

/**
 * Get a user-friendly error message from an error object
 * Handles timeout errors, network errors, and other common error types
 * 
 * @param error - The error object (can be AxiosError, Error, or any)
 * @param fallbackMessage - Default message if error type is unknown
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: any, fallbackMessage = 'An error occurred'): string {
  // Handle timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'There is a problem with the network connection. Please try again.'
  }

  // Handle network errors (no response from server)
  if (error?.request && !error?.response) {
    return 'There is a problem with the network connection. Please try again.'
  }

  // Handle axios errors with response
  if (error?.response) {
    const status = error.response.status
    const data = error.response.data

    // Check if there's a custom message from the API
    if (data?.message && typeof data.message === 'string') {
      return data.message
    }

    // Default messages for common HTTP status codes
    switch (status) {
      case 401:
        return 'Session expired. Please login again.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'The requested resource was not found.'
      case 422:
        // Validation errors - return first validation error if available
        if (data?.errors && typeof data.errors === 'object') {
          const firstError = Object.values(data.errors)[0]
          if (Array.isArray(firstError) && firstError.length > 0) {
            return firstError[0]
          }
        }
        return 'Validation failed. Please check your input.'
      case 429:
        return 'Too many requests. Please try again later.'
      case 500:
        return 'Server error. Please try again.'
      case 503:
        return 'Service temporarily unavailable. Please try again later.'
      default:
        return fallbackMessage
    }
  }

  // Handle regular Error objects
  if (error instanceof Error) {
    // Check if it's a timeout-related error message
    if (error.message.includes('timeout')) {
      return 'There is a problem with the network connection. Please try again.'
    }
    return error.message || fallbackMessage
  }

  // Handle string errors
  if (typeof error === 'string') {
    if (error.includes('timeout')) {
      return 'There is a problem with the network connection. Please try again.'
    }
    return error
  }

  // Fallback for unknown error types
  return fallbackMessage
}

/**
 * Check if an error is a network/timeout error
 * 
 * @param error - The error object to check
 * @returns true if the error is network or timeout related
 */
export function isNetworkError(error: any): boolean {
  return (
    error?.code === 'ECONNABORTED' ||
    error?.message?.includes('timeout') ||
    (error?.request && !error?.response)
  )
}

/**
 * Check if an error is a validation error (HTTP 422)
 * 
 * @param error - The error object to check
 * @returns true if the error is a validation error
 */
export function isValidationError(error: any): boolean {
  return error?.response?.status === 422
}

/**
 * Get validation errors from a 422 response
 * 
 * @param error - The error object
 * @returns An object containing field-specific validation errors
 */
export function getValidationErrors(error: any): Record<string, string[]> {
  if (isValidationError(error) && error.response?.data?.errors) {
    return error.response.data.errors
  }
  return {}
}
